import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Badge from "react-bootstrap/Badge";
import {
    createColumnHelper,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    TableState,
    useReactTable
} from "@tanstack/react-table";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { resources } from "@constants/hardcoded-strings";
import { MANAGEMENT_CONSOLE_AXN_ADMIN_SECURITY_RULES_DETAILS_URL } from "@constants/routes";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { AxnAdminSecurityRule } from "@idw-types/data/management-console/axnadmin";
import { useTableFilters } from "@hooks/use-table-filters";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import { injectAxnAdminSecurityRuleId } from "@utils/parameter-injector";
import { navigateToUtil } from "@utils/navigation-util";
import { TableCellWithButton } from "@containers/common/table-cell-with-button/table-cell-with-button";
import { ButtonSizeTypes } from "@enums/button-size-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import styles from "./security-rules-table-container.less";
import { useMemo } from "react";
import { handleSetColumnFiltersChange, handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";
import { ENTITY_STATUS_OPTIONS } from "@constants/entity-status-options";

type SecurityRulesTableContainerProps = {
    securityRules: Array<AxnAdminSecurityRule>;
    tableState: TableState;
};

export const SecurityRulesTableContainer = (props: SecurityRulesTableContainerProps) => {
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const dispatch = useDispatch();

    const handleViewOnClick = (securityRule: AxnAdminSecurityRule) => {
        const url = injectAxnAdminSecurityRuleId(MANAGEMENT_CONSOLE_AXN_ADMIN_SECURITY_RULES_DETAILS_URL, securityRule.id);
        navigateToUtil(url, navigate, routerLocation.pathname);
    };

    const { securityRules, tableState } = props;

    const columnHelper = createColumnHelper<AxnAdminSecurityRule>();
    const columns = useMemo(() => ([
        columnHelper.display({
            id: "action",
            header: "",
            enableSorting: false,
            enableColumnFilter: false,
            cell: (props) => (
                <TableCellWithButton
                    id="view-security-rule-button"
                    size={ButtonSizeTypes.SMALL}
                    variant={"outline-primary"}
                    icon={faEye}
                    text={""}
                    originalObject={props.row.original}
                    onClick={handleViewOnClick}
                />
            )
        }),
        columnHelper.accessor("id", {
            header: resources.tables.securityRules.columns.id.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("className", {
            header: resources.tables.securityRules.columns.className.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("pathText", {
            header: resources.tables.securityRules.columns.pathText.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("operationType", {
            header: resources.tables.securityRules.columns.operationType.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("status", {
            header: resources.tables.workflowTemplates.columns.status.header,
            filterFn: "singleSelectFilter",
            meta: {
                filterOptions: ENTITY_STATUS_OPTIONS
            },
            cell: (props) => {
                const value = props.getValue();
                switch (value) {
                    case EntityStatusTypes.ACTIVE:
                        return <Badge bg={"success"}>{resources.common.active}</Badge>;
                    case EntityStatusTypes.INACTIVE:
                        return <Badge bg={"danger"}>{resources.common.inactive}</Badge>;
                    default:
                        return <span>{resources.common.notFound}</span>;
                }
            }
        }),
        columnHelper.accessor("ruleName", {
            header: resources.tables.securityRules.columns.ruleName.header,
            filterFn: "fuzzyText"
        })
    ]), [securityRules]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: securityRules,
        state: tableState,
        filterFns: filterTypes,
        sortDescFirst: false,
        autoResetPageIndex: true,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.SECURITY_RULES, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, TableSettingsTableTypes.SECURITY_RULES, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.SECURITY_RULES, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer>
            <IdwTable
                id="security-rules"
                className={styles.securityRulesTable}
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"security-rules"}
            />
            <PaginationContainer
                idPrefix="security-rules"
                useTableResult={useTableResult}
            />
        </IdwTableContainer>
    );
};